import { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTRPC } from "@/trpc";
import { useQuery } from "@tanstack/react-query";
import {
  Building,
  Clock,
  ClipboardList,
  DollarSign,
  FileText,
  MapPin,
  ShieldCheck,
  Star,
  Stethoscope,
  AlertCircle,
} from "lucide-react";
import Loading from "@/components/Loading";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RenderHtml } from "@/components/RenderHtml";
import { ExternalJobBadge } from "../components/external-job-badge";
import { getJobDurationLabel } from "@/common/enums/JobDuration";
import { getSalaryRange } from "../utils/get-salary-range";

export const OrgJobPostDetailDialog = ({
  jobId,
  organizationBusinessUnitId,
  open,
  onOpenChange,
}: {
  jobId: string;
  organizationBusinessUnitId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const trpc = useTRPC();
  const {
    data: job,
    isLoading,
    error,
  } = useQuery(
    trpc.jobPost.orgGet.queryOptions(
      { id: jobId, organizationBusinessUnitId },
      { enabled: open },
    ),
  );

  const salaryRange = job ? getSalaryRange(job) : "";

  const location = useMemo(() => {
    if (!job) {
      return "";
    }
    if (job.city) {
      return job.stateCode ? `${job.city}, ${job.stateCode}` : job.city;
    }
    return job.stateCode || "";
  }, [job?.city, job?.stateCode]);

  const facilityName = useMemo(() => {
    if (!job) {
      return "";
    }
    return job?.facility?.name || job.facilityName || "";
  }, [job?.facility?.name, job?.facilityName]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 shrink-0">
          <DialogTitle className="pr-8">
            {isLoading ? "Loading..." : job?.title || "Job Post"}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center p-12">
              <Loading className="h-12 w-12" />
            </div>
          ) : error || !job ? (
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <AlertCircle className="h-10 w-10 text-gray-400 mb-3" />
              <p className="text-sm text-gray-500">
                {error ? "Failed to load job post." : "Job post not found."}
              </p>
            </div>
          ) : (
            <div className="px-6 py-5 space-y-5">
              {/* Status & Badges */}
              <div className="flex flex-wrap gap-2">
                <Badge
                  variant={job.open ? "default" : "secondary"}
                  className="text-xs"
                >
                  {job.open ? "Open" : "Closed"}
                </Badge>
                {salaryRange && (
                  <Badge
                    variant="outline"
                    className="bg-blue-50 border-blue-200 text-blue-700 rounded-full px-3 py-0.5 text-xs font-medium"
                  >
                    <DollarSign className="h-3 w-3 mr-0.5" />
                    {salaryRange}
                  </Badge>
                )}
                {(job.durationIds ?? []).map((durationId) => (
                  <Badge
                    key={durationId}
                    variant="outline"
                    className="bg-green-50 border-green-200 text-green-700 rounded-full px-3 py-0.5 text-xs font-medium"
                  >
                    {getJobDurationLabel(durationId)}
                  </Badge>
                ))}
                {job.facilityType && (
                  <Badge
                    variant="outline"
                    className="rounded-full px-3 py-0.5 text-xs font-medium bg-gray-50 border-gray-200 text-gray-600"
                  >
                    {job.facilityType.name}
                  </Badge>
                )}
                {job.externalUrl && <ExternalJobBadge />}
              </div>

              {/* Key Details */}
              <div className="space-y-2.5">
                {job.healthcareProfessionalTypes &&
                  job.healthcareProfessionalTypes.length > 0 && (
                    <div className="flex items-start gap-2.5">
                      <Stethoscope className="w-4 h-4 mt-0.5 shrink-0 text-blue-500" />
                      <div className="text-sm">
                        <span className="text-gray-500">
                          {job.healthcareProfessionalTypes
                            .map((t) => t.name)
                            .join(", ")}
                          :
                        </span>{" "}
                        <span className="font-medium text-gray-800">
                          {job.specialties && job.specialties.length > 0
                            ? job.specialties.map((s) => s.name).join(", ")
                            : "Not specified"}
                        </span>
                      </div>
                    </div>
                  )}
                {facilityName && (
                  <div className="flex items-center gap-2.5 text-sm">
                    <Building className="w-4 h-4 shrink-0 text-gray-400" />
                    <span className="text-gray-700">
                      {facilityName}
                      {job.facilityType ? ` (${job.facilityType.name})` : ""}
                    </span>
                  </div>
                )}
                {location && (
                  <div className="flex items-center gap-2.5 text-sm">
                    <MapPin className="w-4 h-4 shrink-0 text-gray-400" />
                    <span className="text-gray-700">{location}</span>
                  </div>
                )}
              </div>

              {/* Schedule Description */}
              {job.scheduleDescription && (
                <Card className="shadow-sm border border-gray-200 rounded-xl overflow-hidden">
                  <CardHeader className="pb-2 pt-4 px-4">
                    <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5 text-blue-500" />
                      Work Schedule
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 px-4 pb-4 text-sm text-gray-600 leading-relaxed">
                    <RenderHtml html={job.scheduleDescription} />
                  </CardContent>
                </Card>
              )}

              {/* Responsibilities */}
              {job.responsibilities && job.responsibilities.length > 0 && (
                <Card className="shadow-sm border border-gray-200 rounded-xl overflow-hidden">
                  <CardHeader className="pb-2 pt-4 px-4">
                    <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <ClipboardList className="h-3.5 w-3.5 text-blue-500" />
                      Responsibilities
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 px-4 pb-4">
                    <ul className="space-y-2">
                      {job.responsibilities.map((item: string, i: number) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-sm text-gray-600"
                        >
                          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-400 shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Required Qualifications */}
              {job.requiredQualifications &&
                job.requiredQualifications.length > 0 && (
                  <Card className="shadow-sm border border-gray-200 rounded-xl overflow-hidden">
                    <CardHeader className="pb-2 pt-4 px-4">
                      <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <ShieldCheck className="h-3.5 w-3.5 text-blue-500" />
                        Required Qualifications
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 px-4 pb-4">
                      <ul className="space-y-2">
                        {job.requiredQualifications.map(
                          (item: string, i: number) => (
                            <li
                              key={i}
                              className="flex items-start gap-2 text-sm text-gray-600"
                            >
                              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-400 shrink-0" />
                              {item}
                            </li>
                          ),
                        )}
                      </ul>
                    </CardContent>
                  </Card>
                )}

              {/* Preferred Qualifications */}
              {job.preferredQualifications &&
                job.preferredQualifications.length > 0 && (
                  <Card className="shadow-sm border border-gray-200 rounded-xl overflow-hidden">
                    <CardHeader className="pb-2 pt-4 px-4">
                      <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <Star className="h-3.5 w-3.5 text-blue-500" />
                        Preferred Qualifications
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 px-4 pb-4">
                      <ul className="space-y-2">
                        {job.preferredQualifications.map(
                          (item: string, i: number) => (
                            <li
                              key={i}
                              className="flex items-start gap-2 text-sm text-gray-600"
                            >
                              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-gray-300 shrink-0" />
                              {item}
                            </li>
                          ),
                        )}
                      </ul>
                    </CardContent>
                  </Card>
                )}

              {/* Description */}
              {job.description && (
                <Card className="shadow-sm border border-gray-200 rounded-xl overflow-hidden">
                  <CardHeader className="pb-2 pt-4 px-4">
                    <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <FileText className="h-3.5 w-3.5 text-blue-500" />
                      Job Description
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 px-4 pb-4 text-sm text-gray-600 prose prose-sm max-w-none">
                    <RenderHtml html={job.description} />
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
